import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { CloseOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { covariateAPI } from '../services/api';

const { Text } = Typography;

export interface ModelCovariateBindingEditorHandle {
  save: (options?: { silent?: boolean }) => Promise<void>;
  hasUnsavedChanges: () => boolean;
}

const TRANSFORM_LABELS: Record<string, string> = {
  one_hot: '独热编码',
  value_map: '值映射',
  formula: '公式转换',
};

const POPULATION_LABELS: Record<string, string> = {
  neonate: '新生儿',
  pediatric: '儿童',
  adult: '成人',
};

const formatBindingSaveError = (error: any): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) {
    const blockers = (detail.contract?.blocking_issues || [])
      .map((item: any) => item?.r_variable_name || item?.code)
      .filter(Boolean);
    return blockers.length
      ? `${detail.message}（${blockers.join('、')}）`
      : detail.message;
  }
  return error?.message || '保存失败';
};

// ================================================================
// Model Covariate Binding Editor
// ================================================================
const ModelCovariateBindingEditor = React.forwardRef<ModelCovariateBindingEditorHandle, { modelId: string }>(({ modelId }, ref) => {
  const [bindings, setBindings] = useState<any[]>([]);
  const [allDefs, setAllDefs] = useState<any[]>([]);
  const [contract, setContract] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newRVarInputs, setNewRVarInputs] = useState<Record<number, string>>({});
  const keyRef = useRef(0);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const [bRes, dRes] = await Promise.all([
        covariateAPI.byModel(modelId),
        covariateAPI.definitions(),
      ]);
      setBindings((bRes?.covariates || []).map((b: any) => ({ ...b, _key: keyRef.current++ })));
      setAllDefs(dRes?.definitions || []);
      setContract(bRes?.contract || null);
      setDirty(false);
    } catch { message.error('加载协变量绑定失败'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [modelId]);

  const persistBindings = useCallback(async (options?: { silent?: boolean }) => {
    if (bindings.some((binding) => !binding.covariate_key)) {
      throw new Error('存在未选择协变量的空行，请先补全或删除');
    }
    const cleanBindings = bindings.map(({ _key, ...rest }) => rest);
    const response = await covariateAPI.updateModelBindings(modelId, cleanBindings);
    setContract(response?.contract || null);
    setDirty(false);
    if (!options?.silent) {
      message.success('协变量绑定已保存');
    }
  }, [bindings, modelId]);

  useImperativeHandle(ref, () => ({
    save: async (options) => {
      setSaving(true);
      try {
        await persistBindings(options);
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        if (detail?.contract) setContract(detail.contract);
        message.error(formatBindingSaveError(error));
        throw error;
      } finally {
        setSaving(false);
      }
    },
    hasUnsavedChanges: () => dirty,
  }), [dirty, persistBindings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await persistBindings();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (detail?.contract) setContract(detail.contract);
      message.error(formatBindingSaveError(e));
    } finally {
      setSaving(false);
    }
  };

  const addBinding = () => {
    setDirty(true);
    setBindings([...bindings, {
      _key: keyRef.current++,
      covariate_key: '', r_variable_name: '', is_required: true,
      population_tag: null, conversion_formula: '',
      transform_type: null, transform_config: null, default_r_values: null,
    }]);
  };

  const removeBinding = (idx: number) => {
    setDirty(true);
    setBindings(bindings.filter((_, i) => i !== idx));
  };

  const updateBinding = (idx: number, field: string, value: any) => {
    setDirty(true);
    const updated = [...bindings];
    updated[idx] = { ...updated[idx], [field]: value };
    setBindings(updated);
  };

  const updateBindingMulti = (idx: number, fields: Record<string, any>) => {
    setDirty(true);
    const updated = [...bindings];
    updated[idx] = { ...updated[idx], ...fields };
    setBindings(updated);
  };

  // Boolean definitions have an implicit, stable option domain.  They should
  // not need to be changed to `select` just to configure a value map.
  const getDefOptionValues = (covKey: string): string[] => {
    const def = allDefs.find(d => d.key === covKey);
    if (!def) return [];
    if (def.data_type === 'boolean' || def.data_type === 'bool') {
      return ['true', 'false'];
    }
    if (!def.options) return [];
    const opts = typeof def.options === 'string'
      ? (() => { try { return JSON.parse(def.options); } catch { return []; } })()
      : def.options;
    const optionValues: unknown[] = Array.isArray(opts) ? opts : [];
    return optionValues
      .map((o: unknown) => typeof o === 'string' ? o : (o as { value?: unknown } | null)?.value)
      .filter((value: unknown): value is string => value !== undefined && value !== null)
      .map((value: string) => String(value));
  };

  // 已选中的 key 集合，用于过滤下拉选项
  const selectedKeys = new Set(bindings.map(b => b.covariate_key).filter(Boolean));
  const defOptions = allDefs.map(d => ({ label: `${d.label} (${d.key})`, value: d.key, disabled: selectedKeys.has(d.key) }));
  const scriptRVariables = (contract?.r_covariates_raw || []) as string[];
  const configuredRVariables = bindings
    .map((binding) => String(binding.r_variable_name || '').trim())
    .filter(Boolean);
  const rVariableOptions = Array.from(new Set([...scriptRVariables, ...configuredRVariables])).map((name) => ({
    label: scriptRVariables.includes(name) ? name : `${name}（脚本未找到）`,
    value: name,
  }));

  const bindingColumns = [
    {
      title: '协变量', dataIndex: 'covariate_key', width: 180,
      render: (val: string, _: any, idx: number) => (
        <Select size="small" style={{ width: '100%' }} placeholder="协变量" value={val || undefined}
          onChange={v => updateBinding(idx, 'covariate_key', v)} options={defOptions} showSearch optionFilterProp="label" />
      ),
    },
    {
      title: 'R输出变量（精确）', dataIndex: 'r_variable_name', width: 150,
      render: (val: string, _: any, idx: number) => (
        <Select
          size="small"
          style={{ width: '100%' }}
          value={val || undefined}
          allowClear
          showSearch
          placeholder="仅患者资料"
          options={rVariableOptions}
          onChange={value => updateBinding(idx, 'r_variable_name', value || null)}
        />
      ),
    },
    {
      title: '适用人群', dataIndex: 'population_tag', width: 100,
      render: (val: string, _: any, idx: number) => (
        <Select size="small" style={{ width: '100%' }} value={val || 'all'}
          onChange={v => updateBinding(idx, 'population_tag', v === 'all' ? null : v)}
          options={[
            { value: 'all', label: '通用' },
            { value: 'neonate', label: '新生儿' },
            { value: 'pediatric', label: '儿童' },
            { value: 'adult', label: '成人' },
          ]} />
      ),
    },
    {
      title: '变换类型', dataIndex: 'transform_type', width: 110,
      render: (val: string, _: any, idx: number) => (
        <Select size="small" style={{ width: '100%' }} value={val || undefined} allowClear placeholder="无"
          onChange={v => {
            updateBindingMulti(idx, { transform_type: v || null, transform_config: null, default_r_values: null });
          }}
          options={[
            { value: 'one_hot', label: '独热编码' },
            { value: 'value_map', label: '值映射' },
            { value: 'formula', label: '公式转换' },
          ]} />
      ),
    },
    {
      title: '转换公式', dataIndex: 'conversion_formula', width: 120,
      render: (val: string, _: any, idx: number) => (
        <Input size="small" placeholder="转换公式" value={val || ''}
          onChange={e => updateBinding(idx, 'conversion_formula', e.target.value)} />
      ),
    },
    {
      title: '默认R值', dataIndex: 'default_r_values', width: 160,
      render: (val: any, record: any) => {
        if (record.transform_type === 'one_hot' || record.transform_type === 'value_map') {
          const cfg = record.transform_config;
          if (!cfg || Object.keys(cfg).length === 0) return <Text type="secondary" style={{ fontSize: 11 }}>展开行编辑</Text>;
          if (val) return <Text code style={{ fontSize: 10 }}>{JSON.stringify(val)}</Text>;
          return <Tag color="orange" style={{ fontSize: 10 }}>已配置映射</Tag>;
        }
        return val ? <Text code style={{ fontSize: 10 }}>{JSON.stringify(val)}</Text> : '-';
      },
    },
    {
      title: '必填', dataIndex: 'is_required', width: 80, align: 'center' as const,
      render: (val: boolean, _: any, idx: number) => (
        <Switch size="small" checked={val}
          onChange={v => updateBinding(idx, 'is_required', v)}
          checkedChildren="必填" unCheckedChildren="选填" />
      ),
    },
    {
      title: '', width: 50,
      render: (_: any, __: any, idx: number) => (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeBinding(idx)} />
      ),
    },
  ];

  // Expandable row render for one_hot / value_map transform editing
  const renderExpanded = (record: any, idx: number) => {
    if (record.transform_type === 'one_hot') {
      const optionValues = getDefOptionValues(record.covariate_key);
      const config: Record<string, Record<string, number>> = record.transform_config || {};
      // Derive R variable names from existing config
      const rVarSet = new Set<string>();
      Object.values(config).forEach((mapping: any) => {
        if (typeof mapping === 'object' && mapping !== null) Object.keys(mapping).forEach(k => rVarSet.add(k));
      });
      const rVarNames = Array.from(rVarSet);
      const allRowKeys = [...optionValues.filter((v: string) => v !== 'default'), 'default'];

      const updateCell = (rowKey: string, rVar: string, val: number) => {
        const newConfig = { ...config };
        newConfig[rowKey] = { ...(newConfig[rowKey] || {}), [rVar]: val };
        const defaultRow = newConfig['default'] || {};
        updateBindingMulti(idx, {
          transform_config: newConfig,
          default_r_values: Object.keys(defaultRow).length > 0 ? defaultRow : null,
        });
      };

      const addRVar = () => {
        const name = newRVarInputs[idx]?.trim();
        if (!name) return;
        if (rVarNames.includes(name)) { message.warning('该R变量名已存在'); return; }
        const newConfig = { ...config };
        allRowKeys.forEach(k => { newConfig[k] = { ...(newConfig[k] || {}), [name]: 0 }; });
        updateBindingMulti(idx, {
          transform_config: newConfig,
          default_r_values: newConfig['default'] || null,
        });
        setNewRVarInputs(prev => ({ ...prev, [idx]: '' }));
      };

      const removeRVar = (rVar: string) => {
        const newConfig: Record<string, Record<string, number>> = {};
        Object.entries(config).forEach(([k, mapping]) => {
          if (typeof mapping === 'object' && mapping !== null) {
            const { [rVar]: _, ...rest } = mapping;
            newConfig[k] = rest;
          }
        });
        updateBindingMulti(idx, {
          transform_config: newConfig,
          default_r_values: newConfig['default'] || null,
        });
      };

      const matrixColumns: any[] = [
        {
          title: '选项值', dataIndex: '_rowKey', width: 120, fixed: 'left' as const,
          render: (v: string) => v === 'default' ? <Text strong>默认值</Text> : v,
        },
        ...rVarNames.map(rVar => ({
          title: (
            <Space size={2}>
              <Text style={{ fontSize: 12 }}>{rVar}</Text>
              <Popconfirm title={`删除列 ${rVar}？`} onConfirm={() => removeRVar(rVar)}>
                <CloseOutlined style={{ fontSize: 10, color: '#999', cursor: 'pointer' }} />
              </Popconfirm>
            </Space>
          ),
          dataIndex: rVar,
          width: 90,
          render: (_: any, row: any) => (
            <InputNumber size="small" style={{ width: 60 }} min={0} max={1}
              value={(config[row._rowKey] || {})[rVar] ?? 0}
              onChange={v => updateCell(row._rowKey, rVar, v ?? 0)} />
          ),
        })),
      ];

      const matrixData = allRowKeys.map(k => ({ _rowKey: k }));

      return (
        <div style={{ padding: '8px 0' }}>
          <Space style={{ marginBottom: 8 }}>
            <Text strong style={{ fontSize: 12 }}>独热编码配置</Text>
            <Input size="small" style={{ width: 120 }} placeholder="R变量名"
              value={newRVarInputs[idx] || ''}
              onChange={e => setNewRVarInputs(prev => ({ ...prev, [idx]: e.target.value }))}
              onPressEnter={addRVar} />
            <Button size="small" icon={<PlusOutlined />} onClick={addRVar}>添加列</Button>
          </Space>
          {optionValues.length === 0 && (
            <Alert type="warning" message="请先在协变量定义中配置选项列表" style={{ marginBottom: 8 }} />
          )}
          <Table size="small" dataSource={matrixData} columns={matrixColumns}
            rowKey="_rowKey" pagination={false} bordered />
        </div>
      );
    }

    if (record.transform_type === 'value_map') {
      const optionValues = getDefOptionValues(record.covariate_key);
      const config: Record<string, number> = record.transform_config || {};

      const updateMapValue = (optVal: string, numVal: number | null) => {
        const newConfig = { ...config, [optVal]: numVal ?? 0 };
        updateBindingMulti(idx, { transform_config: newConfig });
      };

      const mapData = optionValues.map((v: string) => ({ optionValue: v, rValue: config[v] }));

      return (
        <div style={{ padding: '8px 0' }}>
          <Text strong style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>值映射配置</Text>
          {optionValues.length === 0 && (
            <Alert type="warning" message="请先在协变量定义中配置选项列表" style={{ marginBottom: 8 }} />
          )}
          <Table size="small" dataSource={mapData} rowKey="optionValue" pagination={false} bordered
            columns={[
              { title: '选项值', dataIndex: 'optionValue', width: 150 },
              {
                title: 'R数值', dataIndex: 'rValue', width: 120,
                render: (_: any, row: any) => (
                  <InputNumber size="small" style={{ width: 100 }}
                    value={config[row.optionValue]}
                    onChange={v => updateMapValue(row.optionValue, v)} />
                ),
              },
            ]} />
        </div>
      );
    }

    return null;
  };

  const adoptAliasSuggestion = (row: any) => {
    if (!row?.covariate_key || !row?.r_variable_name) return;
    setDirty(true);
    setBindings((current) => {
      const existingIndex = current.findIndex(item => item.covariate_key === row.covariate_key);
      if (existingIndex >= 0) {
        return current.map((item, index) => index === existingIndex
          ? { ...item, r_variable_name: row.r_variable_name }
          : item);
      }
      return [...current, {
        _key: keyRef.current++,
        covariate_key: row.covariate_key,
        r_variable_name: row.r_variable_name,
        is_required: true,
        population_tag: null,
        conversion_formula: '',
        transform_type: null,
        transform_config: null,
        default_r_values: null,
      }];
    });
  };

  const contractStatusMeta: Record<string, { label: string; color: string }> = {
    confirmed: { label: '已确认', color: 'green' },
    alias_suggested: { label: '别名建议，待确认', color: 'gold' },
    model_override: { label: '模型特例', color: 'purple' },
    parse_fallback: { label: 'DB 回退', color: 'blue' },
    unmapped: { label: '未关联', color: 'red' },
    duplicate_output: { label: '重复输出', color: 'red' },
    stale_binding: { label: '过期绑定', color: 'red' },
    binding_missing_r_variable: { label: '缺少 R 变量', color: 'red' },
    inactive_definition: { label: '协变量已停用', color: 'red' },
    unknown_definition: { label: '协变量不存在', color: 'red' },
    normalized_name_collision: { label: '名称冲突', color: 'red' },
    invalid_conversion_formula: { label: '转换公式无效', color: 'red' },
    invalid_transform_config: { label: '转换配置无效', color: 'red' },
  };

  if (loading) return <Spin />;

  return (
    <>
      <Alert
        message="核心计算仅使用已确认的模型绑定；全局 R 别名只生成建议，不会在运行时猜测变量名。"
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
      />
      {contract && (
        <Card
          size="small"
          title={`R 脚本协变量 · ${contract.summary?.confirmed_count || 0}/${contract.summary?.raw_count || 0} 已确认`}
          style={{ marginBottom: 12 }}
          extra={contract.is_ready
            ? <Tag color="green">契约可运行</Tag>
            : <Tag color="red">{contract.summary?.blocking_count || 0} 个阻断项</Tag>}
        >
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            {(contract.rows || []).map((row: any) => {
              const meta = contractStatusMeta[row.status] || { label: row.status, color: 'default' };
              return (
                <div key={`${row.r_variable_name}-${row.covariate_key || 'none'}`}
                  style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Text code>{row.r_variable_name}</Text>
                  <Text type="secondary">→</Text>
                  <Text>{row.label || row.covariate_key || '未关联'}</Text>
                  {row.covariate_key && <Text type="secondary">({row.covariate_key})</Text>}
                  <Tag color={meta.color}>{meta.label}</Tag>
                  {row.status === 'alias_suggested' && (
                    <Button size="small" type="link" onClick={() => adoptAliasSuggestion(row)}>采用建议</Button>
                  )}
                </div>
              );
            })}
            {!(contract.rows || []).length && (
              <Text type="secondary">R 脚本未声明协变量。</Text>
            )}
            {(contract.extra_bindings || []).map((row: any, index: number) => {
              const meta = contractStatusMeta[row.status] || { label: row.status, color: 'red' };
              return (
                <div key={`extra-${row.status}-${row.covariate_key}-${row.r_variable_name || ''}-${index}`}>
                  <Tag color={meta.color}>{meta.label}</Tag>
                  <Text>{row.covariate_key}</Text>
                  {row.r_variable_name && <Text code style={{ marginLeft: 8 }}>{row.r_variable_name}</Text>}
                  {row.message && <Text type="danger" style={{ marginLeft: 8 }}>{row.message}</Text>}
                </div>
              );
            })}
            {(contract.patient_input_bindings || []).length > 0 && (
              <div>
                <Tag color="blue">仅患者资料</Tag>
                <Text type="secondary">
                  {(contract.patient_input_bindings || [])
                    .map((row: any) => row.covariate_key)
                    .filter(Boolean)
                    .join('、')}
                  （不直接传入 R）
                </Text>
              </div>
            )}
          </Space>
        </Card>
      )}
      <Table
        size="small"
        dataSource={bindings}
        columns={bindingColumns}
        rowKey="_key"
        pagination={false}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (record: any, index: number) => renderExpanded(record, index),
          rowExpandable: (record: any) => record.transform_type === 'one_hot' || record.transform_type === 'value_map',
          defaultExpandAllRows: true,
        }}
      />
      <Space style={{ marginTop: 8 }}>
        <Button size="small" icon={<PlusOutlined />} onClick={addBinding}>添加绑定</Button>
        <Button size="small" type="primary" onClick={handleSave} loading={saving}>保存绑定</Button>
      </Space>
    </>
  );
});
ModelCovariateBindingEditor.displayName = 'ModelCovariateBindingEditor';

export default ModelCovariateBindingEditor;
